export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  category: string;
  image: string;
  tags: string[];
  readTime: string;
}

export const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: 'Sådan Vælger Du Den Rigtige Sadel Til Din Hest',
    slug: 'saadan-vaelger-du-den-rigtige-sadel',
    excerpt: 'En velpassende sadel er afgørende for både rytterens komfort og hestens velbefindende. Lær hvordan du finder den perfekte sadel til dig og din hest.',
    author: 'Anna Nielsen',
    date: '2025-10-15',
    category: 'Udstyr',
    image: 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=1200&h=600&fit=crop&q=80',
    tags: ['Sadler', 'Udstyr', 'Guide'],
    readTime: '8 min',
    content: `
# Sådan Vælger Du Den Rigtige Sadel Til Din Hest

En sadel er en af de vigtigste investeringer for enhver rytter. Den rette sadel kan forbedre din ridning, beskytte din hests ryg og give dig bedre kontrol og balance.

## Hvorfor Er Sadeltilpasning Vigtig?

En dårligt tilpasset sadel kan forårsage:
- Rygproblemer hos hesten
- Ubehag og modvilje mod at blive redet
- Rygsmerter hos rytteren
- Dårlig balance og holdning

**Vigtigt**: En korrekt sadeltilpasning er afgørende for både hestens og rytterens velbefindende.

## Typer Af Sadler

### 1. Dressursadler
Designet til dressurryttere med et dybt sæde og lange, lige flapper. Giver tæt kontakt og gør det nemmere at holde den korrekte position.

**Fordele:**
- Dybt sæde for bedre stabilitet
- Lange flapper for bedre benstøtte
- Tæt kontakt med hesten

### 2. Springsadler
Med fremadskårne flapper og et fladere sæde, designet til at støtte rytteren i en mere fremadlænet position ved spring.

### 3. Alsidige Sadler
En god mellemting mellem dressur og spring. Perfekt til ryttere der dyrker flere discipliner eller fritidsridning.

### 4. Western Sadler
Traditionelle arbejdssadler med et horn foran. Designet til komfort over lange distancer.

## Sådan Måler Du Din Hests Ryg

1. **Withers-højde**: Mål fra grunden til withers (makkens højeste punkt)
2. **Ryggens bredde**: Brug en sadeltilpasningsform eller bøjelig lineal
3. **Ryggens længde**: Fra withers til hoften
4. **Skuldervinkel**: Tjek hvordan skulderbladet bevæger sig

## Tips Til At Finde Den Rigtige Sadel

- **Prøv før du køber**: Mange butikker tilbyder prøveperioder
- **Få professionel hjælp**: En sadelmager kan hjælpe med tilpasning
- **Tjek hestens reaktion**: Observer om hesten går bedre med den nye sadel
- **Investér i kvalitet**: En god sadel holder i mange år

## Vedligeholdelse

- Rens sadlen regelmæssigt med sadelsæbe
- Brug læderfedt for at holde læderet smidigt
- Opbevar på et sadelbukke i et tørt rum
- Få sadlen tjekket årligt af en professionel

## Konklusion

En god sadel er en langsigtet investering i både din og din hests sundhed og komfort. Tag dig tid til at finde den rigtige!

Se også [sadler til dressur, spring og western](/sadler) og sammenlign priser fra danske forhandlere.

✅ **Husk**: Korrekt sadeltilpasning kan forhindre mange problemer
❌ **Undgå**: At købe en sadel uden at prøve den først
    `
  },
  {
    id: '2',
    title: '10 Essentielle Plejeværktøjer Enhver Hesteejer Bør Have',
    slug: '10-essentielle-plejevarktojer',
    excerpt: 'Fra daglig pleje til særlige behandlinger - disse værktøjer er uundværlige for at holde din hest sund, ren og glad.',
    author: 'Lars Pedersen',
    date: '2025-10-12',
    category: 'Pleje',
    image: 'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?w=1200&h=600&fit=crop&q=80',
    tags: ['Pleje', 'Udstyr', 'Guide'],
    readTime: '6 min',
    content: `
# 10 Essentielle Plejeværktøjer Enhver Hesteejer Bør Have

Regelmæssig pleje er afgørende for din hests sundhed og velbefindende. Her er de vigtigste værktøjer du skal have i dit plejekabinet.

## 1. Stryglebørste (Curry Comb)

Den første i plejerækken! Bruges i cirkulære bevægelser til at løsne snavs og stimulere huden. Findes i gummi, metal eller plastik.

**Tip**: Brug aldrig metal curry comb på ben eller ansigt.

## 2. Hård Børste (Dandy Brush)

Med stive børstehår til at fjerne løst snavs og slam. Perfekt efter stryglebørsten.

## 3. Blød Børste (Body Brush)

Den fineste børste med bløde børstehår. Giver pelsen det sidste finish og er god til følsomme områder.

## 4. Mankekam

Til at rede man og hale. Vælg en med afrundede tænder for at undgå at brække hårene.

## 5. Hovkrog

**Det vigtigste værktøj!** Tjek hove dagligt for sten, snavs og eventuelle skader.

### Sådan bruger du en hovkrog:
- Hold benet støt
- Rens fra hæl mod tå
- Vær forsigtig omkring strålen
- Tjek for varme eller lugt

## 6. Svamp eller Klud

Til rengøring af øjne, næsebor og dock-område. Brug separate svampe til ansigt og bagdel!

## 7. Hovolie eller Hovfedt

Holder hove sunde og forhindrer tørhed og revner, især i tørre perioder.

## 8. Manespray eller Conditioner

Forebygger knuder i man og hale og gør dem lettere at rede.

## 9. Schweiskniv

Til at fjerne vand og sved efter ridning eller bad, især om vinteren.

## 10. Plejetaske

Hold alt samlet i en praktisk taske eller spand. Gør det nemt at have alt med til stalden.

## Bonustips: Opbevaring

- Rens værktøjer regelmæssigt
- Lad børster tørre ordentligt
- Udskift slidte værktøjer
- Mærk dit udstyr med navn

Med disse grundlæggende værktøjer er du godt rustet til at holde din hest ren, sund og glad!

Se også [plejeudstyr og hovpleje](/pleje) samt guiden [Hovpleje 101](/blog/hovpleje-101-guide).
    `
  },
  {
    id: '3',
    title: 'Guide: Vinterfoder Til Heste - Hvad Du Skal Vide',
    slug: 'vinterfoder-til-heste-guide',
    excerpt: 'Når temperaturen falder, skal hestens foder tilpasses. Lær hvordan du fodrer optimalt gennem vinteren for at holde din hest varm og sund.',
    author: 'Mette Sørensen',
    date: '2025-10-10',
    category: 'Foder',
    image: 'https://images.unsplash.com/photo-1598632640487-6ea4a4e8b6bd?w=1200&h=600&fit=crop&q=80',
    tags: ['Foder', 'Vinter', 'Ernæring'],
    readTime: '7 min',
    content: `
# Guide: Vinterfoder Til Heste - Hvad Du Skal Vide

Vinteren stiller særlige krav til hestens fodring. Her er alt du skal vide om at holde din hest velfodret og varm gennem de kolde måneder.

## Hvorfor Ændres Foderbehovet Om Vinteren?

Heste bruger meget energi på at holde sig varme når temperaturen falder. Deres energibehov kan stige med op til 25% i kolde perioder.

## Grovfoder Er Nøglen

### Hø - Hestens Varmekilde

Hø er den bedste måde at holde din hest varm på! Når heste fordøjer hø, producerer det varme indefra gennem fermenteringsprocessen i tyktarmen.

**Fodertip**:
- Øg hømængden med 10-15% i frost
- Fodre mere hø, ikke nødvendigvis mere kraftfoder
- Sørg for fri adgang til hø i kolde nætter

## Vandindtagelse - Ofte Overset!

Heste drikker mindre når vandet er iskoldt, hvilket kan føre til kolik.

**Løsninger**:
- Brug vandvarmere i spandene
- Tjek vand flere gange dagligt
- Tilbyd lunkent vand om morgenen
- Overvej fugtet foder

## Tilpasning Af Kraftfoder

### Hvornår skal du øge kraftfoder?

Kun hvis hesten:
- Taber sig på trods af ekstra hø
- Er i hårdt arbejde
- Er gammel eller syg
- Har svært ved at holde varmen

### Typer af vinterfoder:

**1. Vegetabilsk Olie**
- Højt energiindhold
- Giver glat pels
- Tilsæt gradvist (start med 50ml/dag)

**2. Sukkerroefliber**
- Langsom energifrigivelse
- Kan blødes op i varmt vand
- God for heste med tandproblemer

**3. Vitamintilskud**
- E-vitamin særligt vigtigt om vinteren
- Selen for muskelvedligeholdelse
- Biotin for hovkvalitet

## Særlige Hensyn

### Ældre Heste
- Kan have brug for mere energitæt foder
- Overvej senior-foder med let fordøjelige fibre
- Ekstra tæpper kan reducere foderbehov

### Heste På Fold
- Har større energibehov end staldstående
- Fri adgang til læ er vigtigt
- Ekstra hø i hækasser på folden

## Tegn På Korrekt Fodring

✅ Hesten holder vægten
✅ Blank, glat vinterpels
✅ God energi og humør
✅ Normal gødningskonsistens
✅ Varme ører (tegn på god cirkulation)

## Almindelige Fejl At Undgå

❌ At øge kraftfoder for hurtigt
❌ At glemme at øge vandindtagelsen
❌ At fodre koldt foder fra frosten
❌ At springe målinger af kropskondition over

## Konklusion

Husk: Hø er din bedste ven om vinteren! Det holder hesten varm indefra, beskæftiger den og fremmer sund fordøjelse. Tilpas kraftfoder efter individuelle behov, og hold altid et vågent øje med din hests kondition gennem vinteren.

Se også [hestefoder og tilskud](/foder).
    `
  },
  {
    id: '4',
    title: 'Ridebeklædning Guide: Investér I Kvalitet Der Holder',
    slug: 'ridebeklædning-guide-kvalitet',
    excerpt: 'Korrekt ridebeklædning handler ikke kun om stil - det handler om sikkerhed, komfort og performance. Her er hvad du skal kigge efter.',
    author: 'Sophie Hansen',
    date: '2025-10-08',
    category: 'Beklædning',
    image: 'https://images.unsplash.com/photo-1544717302-de2939b7ef71?w=1200&h=600&fit=crop&q=80',
    tags: ['Ridebeklædning', 'Sikkerhed', 'Guide'],
    readTime: '9 min',
    content: `
# Ridebeklædning Guide: Investér I Kvalitet Der Holder

Når det kommer til ridebeklædning, er det vigtigt at finde balancen mellem stil, komfort og sikkerhed. Her er den ultimative guide til at vælge beklædning der holder i mange år.

## Sikkerhedsudstyret - Aldrig På Kompromis!

### Ridehjelmen - Din Vigtigste Investering

En CE-godkendt ridehjelm kan redde dit liv. Punkt.

**Hvad skal du kigge efter:**
- CE-mærkning eller tilsvarende sikkerhedscertificering
- Korrekt pasform (må ikke kunne drejes på hovedet)
- God ventilation
- Let vægt
- Justerbar nakkerem

**Vigtig regel**: Udskift hjelm efter ethvert fald eller slag!

### Sikkerhedsvest

Anbefales stærkt til:
- Springning
- Cross country
- Unge eller uerfarne heste
- Nervøse ryttere

## Ridebukser - Komfort I Sadlen

### Materialer

**Bomuld/Lycra mix**
- Åndbart
- Behageligt i varme
- Kan krympe ved vask

**Tekniske materialer**
- Fugttransporterende
- Hurtigtørrende
- Holder formen bedre
- Dyrere, men holder længere

### Forskellige Typer

**Fuldsæde (full seat)**
- Ekstra grip på indersiden af lår og sæde
- Perfekt til dressur
- Bedre stabilitet

**Knæ-patch**
- Grip kun ved knæene
- Mere bevægelsesfrihed
- God til spring og allround

## Støvler - Støt Dine Fødder

### Korte Støvler + Chaps
**Fordele:**
- Nemmere at få på
- Mere fleksible
- Billigere
- Nemmere at gå i

**Ulemper:**
- Kan skride ned
- Mindre elegant look

### Lange Støvler
**Fordele:**
- Professionelt udseende
- Bedre støtte til benet
- Varmt om vinteren

**Ulemper:**
- Dyrere
- Kræver indløbning
- Vanskeligere at få på

## Jakker og Overtøj

### Konkurrencejakker
- Traditionelt snit
- Skal sidde perfekt
- Vælg åndbart materiale

### Træningsjakker
- Vind- og vandafvisende
- Bevægelsesfrihed
- Reflekser til mørke dage

### Fleece og Softshell
- Lag-på-lag princippet
- God til kølige dage
- Nem at vaske

## Handsker - Grip Og Varme

**Sommer:**
- Tynde læderhandsker
- God grip
- Åndbare

**Vinter:**
- Isolerede handsker
- Vandafvisende
- Stadig god fingerfølelse

## Budgettips

### Hvor Skal Du Investere?

💰💰💰 **Premium kvalitet:**
- Ridehjelm (sikkerhed!)
- Støvler (holder i 5-10 år)
- Vinterjakke (bruges meget)

💰💰 **Mellem kvalitet:**
- Ridebukser (2-3 par)
- Træningshandsker
- Fleece lag

💰 **Budget venligt:**
- Poloer til træning
- Ekstra sokker
- Enkle handsker til dagligdag

## Plejetips - Få Dit Udstyr Til At Holde

### Ridebukser
- Vask på vrangen ved max 30°
- Undgå tørretumbler
- Brug ikke blødgører (ødelægger grip)

### Læderstøvler
- Rens efter hver tur
- Brug læderolie regelmæssigt
- Opbevar med støvletræer

### Jakker
- Følg vaskeanvisning nøje
- Impregnér regelmæssigt
- Hæng til tørre - brug ikke varme

## Konklusion

God ridebeklædning er en investering der betaler sig over tid. Prioritér altid sikkerhed først, og vælg derefter kvalitet inden for dit budget. Med korrekt pleje kan dit udstyr holde i mange år!

Se også [ridebukser, støvler og hjelme](/ridebeklaedning).
    `
  },
  {
    id: '5',
    title: 'Staldledelse: Sådan Organiserer Du En Effektiv Stald',
    slug: 'staldledelse-organisering',
    excerpt: 'En velorganiseret stald sparer tid, reducerer stress og skaber et bedre miljø for både heste og mennesker. Her er de bedste tips.',
    author: 'Michael Christensen',
    date: '2025-10-05',
    category: 'Stalddrift',
    image: 'https://images.unsplash.com/photo-1516567727245-ad8a49658a7c?w=1200&h=600&fit=crop&q=80',
    tags: ['Stalddrift', 'Organisation', 'Tips'],
    readTime: '10 min',
    content: `
# Staldledelse: Sådan Organiserer Du En Effektiv Stald

En velorganiseret stald gør hverdagen nemmere og skaber et sundere miljø. Her er de bedste strategier for effektiv staldledelse.

## Planlagte Zoner

### 1. Foderområde
- Adskilt fra selelokale (undgå støv i seletøj)
- Sikre opbevaringsbeholdere mod skadedyr
- Tydeligt mærket med fodertider og mængder
- Let at rengøre gulv

### 2. Selelokale
- Sadelstativer i passende højde
- Kroger til hovedtøj og grime
- Opbevaring af rengøringsudstyr
- God ventilation

### 3. Plejeområde
- Opbevaring af plejeværktøj
- Plads til medicin og førstehjælp
- Klippemaskiner og tilbehør
- Adskilt fra foderstøv

### 4. Værktøj og Vedligeholdelse
- Greb, spande, koste
- Opdeling efter størrelse
- Væghængte løsninger
- Let tilgængeligt

## Daglig Rutine - Tidssparende Tips

### Morgenrutine (30-45 min per hest)
1. Tjek generel tilstand
2. Fodre efter plan
3. Mug boks
4. Frisk vand
5. Læg ud til fold

### Aftenrutine (20-30 min per hest)
1. Tag ind fra fold
2. Aftenfodring
3. Tjek vand
4. Tjek for skader
5. Godnatlys

## Smart Opbevaring

### Foderspande
- Farvekod forskellige typer foder
- Lufttætte beholdere
- Mærk med indhold og dato
- Opbevar køligt og tørt

### Tæpper og Dækkener
- Hylder eller kasser per hest
- Navneskilte
- Separate winter/summer storage
- Ren før opbevaring

## Hygiejne Og Rengøring

### Dagligt
- Mug bokse grundigt
- Fjern vådt halm/spåner
- Fyld vand
- Fej midtergange

### Ugentligt
- Dybstrøelse gennemgang
- Rens vandspande grundigt
- Fej lofter/vægge for spindelvæv
- Tøm affaldsbeholdere

### Månedligt
- Grundig rengøring af selelokale
- Tjek for skadedyr
- Gennemgang af sikkerhedsudstyr
- Vedligehold af hegn og låger

## Sikkerhedsforanstaltninger

### Brandforebyggelse
- Røgalarmer installeret
- Ildslukker tilgængelige
- Ingen rygning i stalden
- Elektriske installationer tjekket årligt
- Nødplan opdateret

### Førstehjælp
- Førstehjælpskasse til heste
- Dyrlægens nummer tydeligt opslået
- Grundlæggende førstehjælpsviden
- Hestens vaccinationer opdateret

## Dokumentation

### Hvad skal du føre?

**Per hest:**
- Fodringsplan
- Medicin og behandlinger
- Skonings datoer
- Vaccinationer
- Tandlægebesøg
- Dyrlægebesøg

**For stalden:**
- Vedligeholdelseslog
- Leverandører og kontakter
- Udgifter
- Forsikringer

## Miljøvenlig Stalddrift

- Kompostér gødning korrekt
- Genbrugelige vandspande
- LED-belysning
- Regnvandsopsamling til vanding

## Teknologi I Stalden

### Nyttige Apps & Værktøjer
- Fodringsplaner i mobilen
- Automatiske vandere med sensor
- Staldsikkerhed med kameraer
- Temperatursensorer

## Økonomistyring

### Budgettering
- Fastsæt månedligt budget
- Spor alle udgifter
- Sammenlign leverandører
- Køb foder i bulk når muligt

### Spar Penge Uden At Gå På Kompromis
- Købssamarbejder med andre stalde
- Sæsonudsalg på tæpper
- Generiske brands på dagligudstyr
- Men aldrig på foder og medicin!

## Konklusion

En velorganiseret stald kræver en investering af tid i starten, men betaler sig mange gange igen i effektivitet og trivsel. Start med ét område ad gangen, og opbyg gode rutiner gradvist.

Din hest og din ryg vil takke dig!

Se også [staldinventar og hverdagsudstyr](/staldinventar).
    `
  },
  {
    id: '6',
    title: 'Hovpleje 101: Sådan Holder Du Dine Hestes Hove Sunde',
    slug: 'hovpleje-101-guide',
    excerpt: '"Ingen hov, ingen hest" - dette gamle ordsprog holder stadig. Lær de grundlæggende principper for god hovpleje og forebyggelse af almindelige problemer.',
    author: 'Anna Nielsen',
    date: '2025-10-01',
    category: 'Pleje',
    image: 'https://images.unsplash.com/photo-1551884170-09fb70a3a2ed?w=1200&h=600&fit=crop&q=80',
    tags: ['Hovpleje', 'Sundhed', 'Forebyggelse'],
    readTime: '8 min',
    content: `
# Hovpleje 101: Sådan Holder Du Dine Hestes Hove Sunde

"Ingen hov, ingen hest" - et ordsprog enhver hesteejer kender. Korrekt hovpleje er fundamentet for en sund og aktiv hest.

## Hovets Anatomi - Grundlæggende Viden

### Hovens Dele
- **Hovvæg**: Den hårde, beskyttende ydre skal
- **Stråle**: Den V-formede struktur i bunden
- **Hvid linje**: Grænsen mellem hovvæg og sål
- **Sål**: Hovens bund
- **Hovbrusk**: Støddæmpende struktur

## Daglig Hovpleje Rutine

### Morgentjek (5 min per hest)

1. **Visuel inspektion**
   - Tjek for varme
   - Se efter hævelser
   - Observer hestens gang

2. **Udkratning**
   - Rens fra hæl mod tå
   - Fjern al snavs, sten og slam
   - Tjek strålen for lugt eller væske
   - Observer hovvæggens tilstand

3. **Hvad at kigge efter:**
   ❌ Dårlig lugt (tegn på stråleråd)
   ❌ Revner i hovvæggen
   ❌ Varme eller pulsering
   ❌ Sort udtræk fra hvid linje
   ❌ Manglende sko eller løse søm

## Professionel Hovbehandling

### Hvor Ofte Skal Hesten Til Smed?

**Beskårede heste**: Hver 6-8 uge
**Barbeinte heste**: Hver 4-6 uge

### Tegn På At Hesten Skal Til Smed

- Sko er løse eller mangler
- Hovene er blevet for lange
- Hesten går anderledes
- Hovvæg vokser ud over sko
- Mere end 8 uger siden sidste besøg

## Almindelige Hovproblemer

### 1. Stråleråd

**Årsager:**
- For våde forhold
- Dårlig hygiejne
- Manglende udkratning

**Forebyggelse:**
- Daglig udkratning
- Tør underlag
- Behandl med strålesalve

### 2. Revner i Hovvæggen

**Kan skyldes:**
- Tørre hove
- Ubalanceret trimning
- Mangelernæring

**Løsning:**
- Hovolie dagligt
- Korrekt biotintilskud
- Professionel hovbehandler

### 3. Abscesser

**Symptomer:**
- Pludselig, kraftig halthed
- Varme i hov
- Pulserende

**Handling:**
- Ring straks til dyrlæge
- Hold hov ren
- Muligvis poultice behandling

## Beskåning vs. Barbenet

### Fordele Ved Sko

✅ Beskyttelse på hårde underlag
✅ Støtte ved ortopædiske problemer
✅ Ekstra grip på glat underlag
✅ Korrektion af gangfejl muligt

### Fordele Ved Barbenet

✅ Naturlig hovmekanik
✅ Bedre blodcirkulation
✅ Billigere vedligehold
✅ Mindre risiko for skader ved spark

## Hovplejeprodukter

### Essentielle Produkter

**Hovolie/Hovfedt**
- Bruges dagligt i tørre perioder
- Hold hove fugtige og fleksible
- Påfør på ren hov

**Strålesalve**
- Forebygger og behandler stråleråd
- Antibakterielle egenskaber
- Påfør efter udkratning

**Stockholm Tjære**
- Traditionel beskyttelse
- God til våde forhold
- Kan være klistret

## Ernæring For Sunde Hove

### Vigtige Næringsstoffer

**Biotin**: 15-30mg dagligt
- Forbedrer hovkvalitet
- Styrker hovvæg
- Kræver 9-12 måneders behandling for effekt

**Zink og Kobber**: Vigtige mineraler
**Methionin**: Svovlholdig aminosyre
**Omega-3**: For antiinflammatorisk effekt

## Underlag I Boksen

### Bedste Underlag For Hove

**Spåner:**
- God dræning
- Absorberer godt
- Skal skiftes ofte

**Halm:**
- Billigere
- Mindre absorberende
- Kan blive våd

**Gummimåtter:**
- Støder hovene
- Let at rengøre
- Kræver mindre strøelse

## Folder Og Hovpleje

### Pas På:
- Mudder i lange perioder = stråleproblemer
- Alt for hårdt underlag = slidte hove
- Sten og skarpe genstande

### Idealfold:
- Varieret underlag
- God dræning
- Læmuligheder
- Daglig tjek for farlige genstande

## Konklusion

Sunde hove kræver:
- ⏰ Daglig opmærksomhed
- 🔧 Regelmæssig professionel pleje
- 🥕 Korrekt ernæring
- 🏠 Godt underlag

Invester tid i hovpleje - det er en af de bedste investeringer du kan gøre for din hests langsigtede sundhed og brugbarhed!

Se også [hovolie, børster og plejeudstyr](/pleje).
    `
  },
  {
    id: '7',
    title: 'Forårsklargøring: Forbered Din Hest På Den Varme Sæson',
    slug: 'foraarsklargoring-hest',
    excerpt: 'Når foråret kommer, skal både hest og udstyr forberedes. Her er den komplette tjekliste for en vellykket overgang fra vinter til sommer.',
    author: 'Sophie Hansen',
    date: '2025-09-28',
    category: 'Årstider',
    image: 'https://images.unsplash.com/photo-1533387520709-752d83de3630?w=1200&h=600&fit=crop&q=80',
    tags: ['Forår', 'Pleje', 'Sæson'],
    readTime: '7 min',
    content: `
# Forårsklargøring: Forbered Din Hest På Den Varme Sæson

Foråret er på vej! Tid til at forberede din hest på den varme sæson. Her er din komplette guide.

## Pelspleje - Fra Vinterpels Til Sommerglans

### Fældning

Heste fælder typisk fra februar til maj. Gør det nemmere:

**Daglige børstning:**
- Brug stryglebørste kraftigt
- Fjern løse hår
- Stimulerer ny vækst

**Klipning:**
- Overvej klipning hvis hesten sveder meget
- Forskellige klippemønstre til forskellige behov
- Husk ekstra tæpper efter klipning

### Bad og Shampoo

- Start med lunkne bade
- Brug hestemild shampoo
- Conditioner til man og hale
- Sørg for hesten tørres ordentligt

## Hovolie - Ekstra Vigtigt Nu!

Forårets skiftende vejr (vådt/tørt) slider på hovene.

**Daglig hovpleje:**
- Påfør hovolie morgen og aften
- Fokusér på kronrand
- Hjælper med fleksibilitet

## Fodertilpasning

### Fra Vinterfoder Til Forårsgræs

⚠️ **VIGTIG**: Introducer græs gradvist!

**Uge 1**: 15-30 min på græs
**Uge 2**: 30-60 min
**Uge 3**: 1-2 timer
**Uge 4+**: Gradvist øge

### Hvorfor Gradvist?

- Forebygger kolik
- Undgår fangbetændelse
- Fordøjelsen skal tilpasse sig
- Reducerer risiko for vægtstigning

### Tilpas Kraftfoder

Når græsset kommer:
- Reducer kraftfoder tilsvarende
- Overvåg kropskondition
- Nogle heste behøver ingenting ud over græs

## Parasitkontrol

### Forårskur

- Orm din hest ved græsgangens start
- Konsultér dyrlæge om program
- Rotering af ormemidler
- Gødningsanalyse anbefales

### Flåter Og Insekter

**Forberedelse:**
- Køb fluespray på forhånd
- Flugitter til bokse
- Fluedækkener til sensitive heste
- Tjek for flåter efter fold

## Udstyr Gennemgang

### Tæpper og Dækkener

**Vintertæpper:**
- Vask grundigt
- Reparer revner
- Impregnér om nødvendligt
- Pak væk i luftige poser

**Sommertæpper:**
- Tjek for skader
- Vask før brug
- Fluemidler kan påføres

### Seletøj Hovedeftersyn

**Tjek alt seletøj for:**
- Revner i læder
- Slidte søm
- Rust på beslag
- Løse dele

**Vedligehold:**
- Grundig rengøring
- Læderoliebehandling
- Udskift slidte dele
- Professionel vurdering af sadel

## Vaccinationer og Sundhed

### Forårscheck

- Tandlæge (1-2 gange årligt)
- Årlige vaccinationer
- Kropsvægt måling
- Generel sundhedstjek

### Årsager Til Dyrlægebesøg

- Hoste eller snot
- Hudproblemer fra vinteren
- Lidelser der blev forværret i kulden
- Generel sommer-check

## Træning Efter Vinteren

### Genopbygning

Hvis hesten har haft vinterpause:

**Uge 1-2**: Lange skridt (30-45 min)
**Uge 3-4**: Let trav tilføjes
**Uge 5-6**: Kort galop
**Uge 7+**: Normal træning

### Undgå Skader

- Opvarm grundigt
- Øg intensitet gradvist
- Observer for halthed
- Lad hesten selv styre tempoet i starten

## Fold-Forberedelse

### Gør Folden Klar

- Reparer hegn
- Fjern farlige genstande
- Tjek vandsystem
- Plant giftige planter væk
- Læmuligheder fra sol

## Mental Velvære

Heste elsker foråret!

**Tegn på glad hest:**
- Leger på folden
- Ruller sig
- Nysgærrig på omgivelser
- God appetit

## Tjekliste: Din Forårsklargøring

✅ Hesten:
- [ ] Ormet
- [ ] Vaccineret
- [ ] Tandtjek
- [ ] Skomager booket
- [ ] Kropskondition vurderet

✅ Udstyr:
- [ ] Vintertæpper vasket og pakket væk
- [ ] Seletøj rengjort og olieret
- [ ] Sommertæpper klar
- [ ] Fluebeskyttelse købt

✅ Stald & Fold:
- [ ] Grundig rengøring
- [ ] Hegn repareret
- [ ] Vandsystem tjekket
- [ ] Læ klargjort

✅ Foder:
- [ ] Gradvis græstilvænning planlagt
- [ ] Kraftfoder justeret
- [ ] Tilskud gennemgået

God forår med din hest! 🌸🐴

Se også [hestetilbehør](/hestetilbehor) og [dækkener og staldinventar](/staldinventar).
    `
  }
];

export function getBlogPost(slug: string): BlogPost | undefined {
  return blogPosts.find(post => post.slug === slug);
}

export function getBlogPostsByCategory(category: string): BlogPost[] {
  return blogPosts.filter(post => post.category === category);
}

export function getRecentBlogPosts(limit: number = 3): BlogPost[] {
  return blogPosts
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, limit);
}

